import React from "react";
import { Stack } from "@UI/Components/Stack/Stack";
import { Box } from "@UI/Components/Box/Box";
import { Drawer } from "@UI/Components/Drawer/Drawer";
import useMediaQuery from "@UI/hooks/useMediaQuery";
import { ServiceOfferDescription } from "@modules/ServiceAssignment/Components/ServiceOfferDescription/ServiceOfferDescription";
import { Button } from "@UI/Components/Button/Button";
import { CandidateProfileSection } from "./CandidatesSection/CandidateProfileSection";
import { CandidateAvatar } from "./CandidatesSection/CandidateAvatar";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { Icon } from "@UI/Components/Icon/Icon";
import { Xmark } from "@UI/Components/Icons/Xmark";
import messages from "./messages";
import { ICandidateOfferModalProps } from "../types";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { useFeatureFlag } from "@Libs/GrowthBook/hooks/useFeatureFlag";
import { SubscriptionDiscount } from "./SubscriptionDiscount/SubscriptionDiscount";
import { useServiceFeesFeature } from "@modules/ServiceFees/hooks/useServiceFeesFeature";

export const CandidateOfferModal = (props: ICandidateOfferModalProps) => {
  const { isOpen, onClose, candidate, onDeclineOfferModalOpen } = props;
  const { isDesktop } = useMediaQuery();
  const { currentUser } = useCurrentUser();
  const isActivationRateEnabled = useFeatureFlag("new-activation-rate");
  const { serviceFeesFeature } = useServiceFeesFeature();
  const serviceFeePercentage =
    serviceFeesFeature?.details?.chargedToClient?.clientServiceFee?.percentage;

  const isSubscribed = currentUser?.subscribed ?? false;

  const serviceOfferId = candidate?.serviceOffer?.id;

  const handleSelectOffer = () => {
    if (window)
      window.location.href = `/contracts/${serviceOfferId}/subscription_fee/new?id=${serviceOfferId}`;
  };

  const handleDeclineOffer = () => {
    onClose();
    onDeclineOfferModalOpen(candidate?.conversationId);
  };

  return (
    <Drawer
      placement={isDesktop ? "right" : "bottom"}
      onClose={onClose}
      isOpen={isOpen}
      size="lg"
      drawerContentContainerStyle={{
        borderRadius: !isDesktop ? "1.25rem" : "none",
        borderBottomRadius: "none",
      }}
    >
      <Stack
        justifyContent="space-between"
        height="full"
        p={{ base: 0, md: 4 }}
      >
        <Box
          as="button"
          w="fit-content"
          data-bs-toggle="offcanvas"
          data-bs-target={`#offcanvas-profil-sp-${candidate?.id}`}
          data-tracking-source="offer_modal"
          aria-controls="offcanvasRight"
          onClick={onClose}
        >
          <CandidateProfileSection
            {...candidate}
            superTalent={false}
            showSeeProfileText={true}
            profileNameStyle={{
              fontSize: "lg",
              lineHeight: { base: "24px", lg: "27px" },
            }}
            badgeStyle={{ fontSize: "xs" }}
            ratingStyle={{ fontSize: "md" }}
            avatarSection={
              <CandidateAvatar
                size="s"
                url={candidate?.profilePicture}
                isSuperTalent={candidate?.superTalent}
              />
            }
          />
        </Box>

        <ServiceOfferDescription
          serviceOfferId={serviceOfferId}
          showServiceFees={isActivationRateEnabled && !isSubscribed}
          compactServiceFees={true}
          showAdvancePayment={false}
          subscriptionDiscountBadge={
            isActivationRateEnabled && candidate?.serviceOffer && serviceFeePercentage !== undefined ? (
              <Box py="2">
                <SubscriptionDiscount
                  w="full"
                  priceCents={isSubscribed
                    ? Math.round(candidate.serviceOffer.rateCents * ((serviceFeePercentage ?? 0) / 100))
                    : (candidate.serviceOffer.serviceFeesCents ?? 0)}
                  rateType={candidate.serviceOffer.rateType}
                  billingUnit={candidate.serviceOffer.billingUnit}
                  currency={candidate.serviceOffer.rateCurrency}
                  expensesFeesCents={candidate.serviceOffer.expensesFeesCents ?? 0}
                  subscribed={isSubscribed}
                  feePercentage={serviceFeePercentage}
                />
              </Box>
            ) : undefined
          }
        />

        <Stack direction="row">
          <Button
            size="lg"
            px="22px"
            background="transparent"
            border="1px"
            borderColor="gray.300"
            minW="fit-content"
            onClick={handleDeclineOffer}
          >
            <Icon as={Xmark} display="block" mr="1.5" />
            <FormattedMessage {...messages["serviceRequest.decline"]} />
          </Button>
          <Button
            data-testid="accept-application"
            as="button"
            w="full"
            size="lg"
            colorScheme="seaGreen"
            flexGrow="1"
            onClick={handleSelectOffer}
          >
            <FormattedMessage {...messages["serviceRequest.acceptOffer"]} />
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
};
